import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api, formatApiErrorDetail } from "../lib/api";
import { trackEvent } from "../lib/analytics";

const AuthContext = createContext(null);

const TOKEN_KEY = "stagvault_token";

export function AuthProvider({ children }) {
  // null = checking, false = unauthenticated, object = user
  const [user, setUser] = useState(null);

  const fetchMe = useCallback(async () => {
    const token = localStorage.getItem(TOKEN_KEY);
    if (!token) {
      setUser(false);
      return;
    }
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
    } catch {
      localStorage.removeItem(TOKEN_KEY);
      setUser(false);
    }
  }, []);

  useEffect(() => {
    fetchMe();
  }, [fetchMe]);

  const login = async (email, password) => {
    try {
      const { data } = await api.post("/auth/login", { email, password });
      localStorage.setItem(TOKEN_KEY, data.token);
      setUser(data.user);
      return { ok: true };
    } catch (e) {
      return { ok: false, error: formatApiErrorDetail(e.response?.data?.detail) || e.message, raw: e };
    }
  };

  const register = async (name, email, password, attribution = {}) => {
    try {
      // Auto-pick up A/B hero variant from localStorage (set by PublicLandingPage on mount)
      const heroVariant = (typeof window !== "undefined" && localStorage.getItem("sv_hero_variant")) || null;
      const { data } = await api.post("/auth/register", {
        name,
        email,
        password,
        utm_source: attribution.utm_source || null,
        utm_medium: attribution.utm_medium || null,
        utm_campaign: attribution.utm_campaign || null,
        utm_content: attribution.utm_content || null,
        utm_term: attribution.utm_term || null,
        landing_page: attribution.landing_page || null,
        turnstile_token: attribution.turnstile_token || null,
        hero_variant_seen: attribution.hero_variant_seen || heroVariant || null,
        grant_code: attribution.grant_code || null,
      });
      localStorage.setItem(TOKEN_KEY, data.token);
      setUser(data.user);
      trackEvent("EmailCaptured", { method: "signup", email });
      return { ok: true, isNew: true };
    } catch (e) {
      return { ok: false, error: formatApiErrorDetail(e.response?.data?.detail) || e.message, raw: e };
    }
  };

  const logout = async () => {
    localStorage.removeItem(TOKEN_KEY);
    setUser(false);
    await api.post("/auth/logout").catch(() => {});
  };

  const updateDisplayName = async (newName) => {
    try {
      const { data } = await api.patch("/auth/me", { display_name: newName });
      setUser(data);
      return { ok: true };
    } catch (e) {
      return { ok: false, error: e.response?.data?.detail || e.message };
    }
  };

  return (
    <AuthContext.Provider value={{ user, login, register, logout, refresh: fetchMe, updateDisplayName }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
